import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, tap } from 'rxjs';
import { environment } from '../../environments/environment';

type TrackedEndpoint =
  | 'GetPatientPrograms'
  | 'GetPatientProgramsDelta'
  | 'GetPatientTargets'
  | 'GetPatientTargetsDelta';

interface EndpointStats {
  callCount: number;
  lastStartedAtMs: number | null;
  lastResponseHash: string | null;
}

@Injectable()
export class PerformanceApiInterceptor implements HttpInterceptor {
  private readonly statsByEndpointAndPatient = new Map<string, EndpointStats>();
  private bannerPrinted = false;

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const endpoint = this.getTrackedEndpoint(request.url);
    if (!endpoint || !this.isLoggingEnabled()) {
      return next.handle(request);
    }

    if (!this.bannerPrinted) {
      this.bannerPrinted = true;
      console.info(
        '[SparkPerf] API diagnostics enabled. In production you can toggle with localStorage.setItem("spark-api-perf-log","1").'
      );
    }

    const patientId = this.extractPatientId(request);
    const statsKey = `${endpoint}|${patientId ?? 'unknown'}`;
    const stats = this.getOrCreateStats(statsKey);
    const startedAtMs = Date.now();
    const msSinceLastCall =
      stats.lastStartedAtMs === null ? null : startedAtMs - stats.lastStartedAtMs;

    stats.callCount += 1;
    stats.lastStartedAtMs = startedAtMs;

    const requestSizeBytes = this.estimateSizeInBytes(request.body);
    const requestDescriptor = `${request.method} ${request.urlWithParams}`;
    const requestLabel = `[SparkPerf] ${endpoint} #${stats.callCount} patient=${patientId ?? 'unknown'}`;
    console.info(
      `${requestLabel} started`,
      { request: requestDescriptor, requestSizeBytes, msSinceLastCall }
    );

    return next.handle(request).pipe(
      tap({
        next: (event: HttpEvent<any>) => {
          if (!(event instanceof HttpResponse)) {
            return;
          }

          const durationMs = Date.now() - startedAtMs;
          const responseSizeBytes = this.estimateSizeInBytes(event.body);
          const responseHash = this.hashValue(event.body);
          const sameAsPreviousResponse =
            stats.lastResponseHash !== null && stats.lastResponseHash === responseHash;

          stats.lastResponseHash = responseHash;

          console.info(
            `${requestLabel} completed`,
            {
              status: event.status,
              durationMs,
              responseSizeBytes,
              responseItemCount: this.estimateItemCount(event.body),
              sameAsPreviousResponse,
            }
          );

          if (sameAsPreviousResponse) {
            console.warn(
              `[SparkPerf] ${endpoint} returned same payload as previous call for patient ${patientId ?? 'unknown'}.`
            );
          }
        },
        error: (error: HttpErrorResponse) => {
          const durationMs = Date.now() - startedAtMs;
          console.warn(
            `${requestLabel} failed`,
            { status: error.status, durationMs, errorMessage: error.message }
          );
        },
      })
    );
  }

  private isLoggingEnabled(): boolean {
    if (!environment.production) {
      return true;
    }
    return localStorage.getItem('spark-api-perf-log') === '1';
  }

  private getTrackedEndpoint(url: string): TrackedEndpoint | null {
    if (url.includes('/GetPatientProgramsDelta')) {
      return 'GetPatientProgramsDelta';
    }
    if (url.includes('/GetPatientPrograms')) {
      return 'GetPatientPrograms';
    }
    if (url.includes('/GetPatientTargetsDelta')) {
      return 'GetPatientTargetsDelta';
    }
    if (url.includes('/GetPatientTargets')) {
      return 'GetPatientTargets';
    }
    return null;
  }

  private extractPatientId(request: HttpRequest<any>): number | null {
    const patientIdFromParams = request.params.get('patientID');
    if (patientIdFromParams) {
      return Number(patientIdFromParams);
    }

    const bodyPatientId = request.body?.patientID ?? request.body?.PatientID;
    if (bodyPatientId !== undefined && bodyPatientId !== null) {
      return Number(bodyPatientId);
    }

    return null;
  }

  private getOrCreateStats(key: string): EndpointStats {
    const existing = this.statsByEndpointAndPatient.get(key);
    if (existing) {
      return existing;
    }

    const created: EndpointStats = {
      callCount: 0,
      lastStartedAtMs: null,
      lastResponseHash: null,
    };
    this.statsByEndpointAndPatient.set(key, created);
    return created;
  }

  private estimateSizeInBytes(value: unknown): number {
    if (value === undefined || value === null) {
      return 0;
    }

    try {
      return new Blob([JSON.stringify(value)]).size;
    } catch {
      return -1;
    }
  }

  private hashValue(value: unknown): string {
    let asText = '';
    try {
      asText = JSON.stringify(value) ?? '';
    } catch {
      asText = String(value);
    }

    let hash = 0;
    for (let i = 0; i < asText.length; i++) {
      hash = (hash << 5) - hash + asText.charCodeAt(i);
      hash |= 0;
    }
    return `${asText.length}:${hash}`;
  }

  private estimateItemCount(value: unknown): number | null {
    if (Array.isArray(value)) {
      return value.length;
    }
    if (value && typeof value === 'object' && Array.isArray((value as any).Items)) {
      return (value as any).Items.length;
    }
    return null;
  }
}
